/**
 * /me/loans/history — Session 3d PR 18.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { LoanHistoryItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyLoanHistoryPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-loan-history", employeeId],
    queryFn: () => session3dApi.listMyLoanHistory(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<LoanHistoryItem>[] = [
    {
      headerKey: "me.loan-history.col.product",
      label: "Product",
      render: (r) => <span className="font-medium">{r.product_name_en}</span>,
    },
    {
      headerKey: "me.loan-history.col.original",
      label: "Original (SAR)",
      render: (r) => <span className="font-mono text-xs">{r.original_amount_sar.toLocaleString()}</span>,
    },
    {
      headerKey: "me.loan-history.col.final",
      label: "Final settlement (SAR)",
      render: (r) => <span className="font-mono text-xs">{r.final_settlement_sar.toLocaleString()}</span>,
    },
    {
      headerKey: "me.loan-history.col.settled",
      label: "Settled",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.settled_date}</span>,
    },
    {
      headerKey: "me.loan-history.col.status",
      label: "Status",
      render: (r) => <span className="text-xs">{r.status}</span>,
    },
  ];

  return (
    <MeListPage<LoanHistoryItem>
      title={t("me.loan-history.title", "My Loan History")}
      subtitle={t("me.loan-history.subtitle", "All past loans (settled, written off) for personal records.")}
      domain="loan-history"
      khidmatPrefill={t("me.loan-history.ask_khidmat", "Show me my loan history.")}
      query={query}
      columns={columns}
      rowKey={(r) => r.application_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.loan-history.title", "My Loan History"),
        window_label: t("me.loan-history.window", "past loans"),
      }}
      emptyHeadline={t("me.loan-history.empty_headline", "No past loans.")}
      emptyBody={t("me.loan-history.empty_body", "Khidmat: nothing to show — you don't have any closed loans.")}
    />
  );
}
